import { Component, ChangeDetectionStrategy, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OcrPrimeStore } from '../../../../store/ocr-prime.store';
import {
  AnchorTreeDecisionNode,
  AnchorTreeLeafNode,
  AnchorTreeNode
} from '../../../../types/anchor-tree';
import { Field } from '../../../../types/fields';

/**
 * Docked inspector panel for the node selected on the anchor-tree canvas
 * (form controls ported from the old recursive tree-node-editor). Emits
 * `changed` with a replacement node on every edit — immutability is handled
 * by the page's draft signal. Structural actions (add branch, delete subtree)
 * live on the canvas; removing a single branch here delegates to the page via
 * `branchRemoveRequested` so the subtree-delete confirmation applies.
 */
@Component({
  selector: 'app-node-inspector',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatIconModule, MatTooltipModule],
  styles: [`:host { display: block; height: 100%; }`],
  template: `
    <div class="flex h-full flex-col overflow-y-auto thin-scroll rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800"
         data-testid="node-inspector">
      <div class="sticky top-0 z-10 flex items-center gap-3 border-b border-zinc-100 dark:border-zinc-700 bg-white/95 dark:bg-zinc-800/95 px-4 py-3 backdrop-blur">
        <span
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300"
          aria-hidden="true"
        >
          <mat-icon class="!text-base !w-4 !h-4 !leading-4">
            {{ node().kind === 'decision' ? 'alt_route' : 'eco' }}
          </mat-icon>
        </span>
        <div class="min-w-0">
          <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            {{ node().kind === 'decision' ? 'Decision node' : 'Leaf' }}
          </p>
          <p class="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100" data-testid="node-inspector-title">
            {{ inspectorTitle() }}
          </p>
        </div>
        <button
          data-testid="node-inspector-close"
          type="button"
          matTooltip="Close inspector"
          aria-label="Close inspector"
          (click)="closeRequested.emit()"
          class="ml-auto flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
        >
          <mat-icon class="!text-base !w-4 !h-4 !leading-4">close</mat-icon>
        </button>
      </div>

      @if (node().kind === 'decision') {
        <div class="space-y-5 p-4" [attr.data-testid]="'node-editor-decision-' + node().id">
          <section class="space-y-4">
            <h3 class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
              <mat-icon class="!text-[13px] !w-3.5 !h-3.5 !leading-[14px]">tune</mat-icon>
              Routing
            </h3>
            <label class="block">
              <span class="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">Decisive anchor field</span>
              <select
                data-testid="node-editor-decisive-field"
                [ngModel]="decision().decisiveAnchorFieldId"
                (ngModelChange)="onDecisiveFieldChange($event)"
                class="w-full rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-2 py-1.5 text-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" disabled>Pick decisive anchor field…</option>
                @for (field of decisionEligibleFields(); track field.id) {
                  <option [value]="field.id">{{ field.name }} ({{ field.type === 'boolean' ? 'boolean' : 'enum' }})</option>
                }
                @if (isUnknownDecisiveField()) {
                  <option [value]="decision().decisiveAnchorFieldId">unknown field ({{ decision().decisiveAnchorFieldId }})</option>
                }
                @if (isIneligibleDecisiveField()) {
                  <option [value]="decision().decisiveAnchorFieldId">{{ decisiveField()?.name }} (not enum/boolean)</option>
                }
              </select>
              @if (!isUnknownDecisiveField() && !isIneligibleDecisiveField()) {
                <span class="mt-1 block text-[11px] text-zinc-500 dark:text-zinc-400">
                  Enum or boolean anchor field this node branches on.
                </span>
              }
            </label>
            @if (isUnknownDecisiveField()) {
              <span class="inline-flex items-center gap-1 rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-300"
                    data-testid="node-editor-unknown-field-warning">
                <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">warning</mat-icon>
                unknown field — deleted?
              </span>
            }
            @if (isIneligibleDecisiveField()) {
              <span class="inline-flex items-center gap-1 rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-300"
                    data-testid="node-editor-ineligible-field-warning">
                <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">warning</mat-icon>
                not enum/boolean — pick another field
              </span>
            }

            <label class="block">
              <span class="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">Routing threshold</span>
              <input
                data-testid="node-editor-threshold"
                type="number" min="0" max="1" step="0.05"
                [ngModel]="decision().threshold"
                (ngModelChange)="patchDecision({ threshold: normalizeThreshold($event) })"
                placeholder="0.8"
                class="w-24 rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-2 py-1 text-sm tabular-nums text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span class="mt-1 block text-[11px] text-zinc-500 dark:text-zinc-400">
                Min match confidence (0–1) to follow a branch. Blank uses the tree default.
              </span>
            </label>

            <label class="block">
              <span class="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">Label (optional)</span>
              <input
                data-testid="node-editor-label"
                type="text"
                [ngModel]="node().label"
                (ngModelChange)="patchNode({ label: $event || undefined })"
                placeholder="Label (optional)"
                class="w-full rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-2 py-1 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
          </section>

          <div class="border-t border-zinc-100 dark:border-zinc-700"></div>

          <!-- Branches -->
          <section>
            <div class="mb-2 flex flex-wrap items-center gap-2">
              <h3 class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
                <mat-icon class="!text-[13px] !w-3.5 !h-3.5 !leading-[14px]">account_tree</mat-icon>
                Branches
                <span class="rounded-full bg-zinc-100 dark:bg-zinc-700 px-1.5 text-[10px] font-semibold text-zinc-500 dark:text-zinc-300 tabular-nums"
                      data-testid="node-editor-branch-count">{{ decision().branches.length }}</span>
              </h3>
              @if (uncoveredValues().length > 0) {
                <span class="inline-flex items-center gap-1 rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-300"
                      data-testid="node-editor-uncovered-values-warning"
                      [matTooltip]="'Not covered: ' + uncoveredValues().join(', ')">
                  <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">warning</mat-icon>
                  {{ uncoveredValues().length }} value(s) without a branch
                </span>
              }
              <div class="ml-auto flex gap-1">
                <button
                  type="button"
                  data-testid="node-editor-add-decision"
                  matTooltip="Add a branch ending in a decision node"
                  aria-label="Add a decision branch"
                  (click)="addBranchRequested.emit('decision')"
                  class="inline-flex cursor-pointer items-center gap-1 rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-1.5 py-0.5 text-[11px] font-medium text-zinc-600 dark:text-zinc-300 transition-colors hover:bg-zinc-50 hover:text-indigo-600 dark:hover:bg-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">alt_route</mat-icon>
                  decision
                </button>
                <button
                  type="button"
                  data-testid="node-editor-add-leaf"
                  matTooltip="Add a branch ending in a leaf"
                  aria-label="Add a leaf branch"
                  (click)="addBranchRequested.emit('leaf')"
                  class="inline-flex cursor-pointer items-center gap-1 rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-1.5 py-0.5 text-[11px] font-medium text-zinc-600 dark:text-zinc-300 transition-colors hover:bg-zinc-50 hover:text-indigo-600 dark:hover:bg-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">eco</mat-icon>
                  leaf
                </button>
              </div>
            </div>
            <div class="space-y-2">
              @for (branch of decision().branches; track $index; let i = $index) {
                <div class="flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50/60 dark:bg-zinc-700/30 px-2.5 py-2" [attr.data-testid]="'node-editor-branch-' + i">
                  <span class="shrink-0 text-xs text-zinc-500 dark:text-zinc-400">when value ≈</span>
                  @if (decisiveFieldKind() === 'boolean') {
                    <div
                      [attr.data-testid]="'node-editor-branch-value-' + i"
                      class="inline-flex overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-600"
                      role="group"
                      aria-label="Expected boolean value"
                    >
                      <button
                        type="button"
                        [attr.data-testid]="'node-editor-branch-value-true-' + i"
                        [disabled]="isValueTakenByOtherBranch(i, 'true')"
                        (click)="patchBranch(i, 'true')"
                        [class]="branch.expectedValue === 'true'
                          ? 'px-2.5 py-1 text-xs font-medium bg-indigo-600 text-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500'
                          : 'px-2.5 py-1 text-xs font-medium bg-white dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-600 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500'"
                      >
                        true
                      </button>
                      <button
                        type="button"
                        [attr.data-testid]="'node-editor-branch-value-false-' + i"
                        [disabled]="isValueTakenByOtherBranch(i, 'false')"
                        (click)="patchBranch(i, 'false')"
                        [class]="branch.expectedValue === 'false'
                          ? 'px-2.5 py-1 text-xs font-medium bg-indigo-600 text-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500'
                          : 'px-2.5 py-1 text-xs font-medium bg-white dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-600 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500'"
                      >
                        false
                      </button>
                    </div>
                  } @else if (decisiveFieldKind() === 'enum') {
                    <select
                      [attr.data-testid]="'node-editor-branch-value-' + i"
                      [ngModel]="branch.expectedValue"
                      (ngModelChange)="patchBranch(i, $event)"
                      class="min-w-0 flex-1 rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-2 py-1 text-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="" disabled>Pick expected value…</option>
                      @for (value of availableEnumValues(i); track value) {
                        <option [value]="value">{{ value }}</option>
                      }
                      @if (branch.expectedValue && !decisiveEnumValues().includes(branch.expectedValue)) {
                        <option [value]="branch.expectedValue">{{ branch.expectedValue }} (no longer an enum value)</option>
                      }
                    </select>
                  } @else {
                    <input
                      [attr.data-testid]="'node-editor-branch-value-' + i"
                      type="text"
                      [ngModel]="branch.expectedValue"
                      (ngModelChange)="patchBranch(i, $event)"
                      placeholder="Expected value"
                      class="min-w-0 flex-1 rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-2 py-1 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  }
                  <button
                    [attr.data-testid]="'node-editor-branch-remove-' + i"
                    type="button"
                    matTooltip="Remove this branch (and its subtree)"
                    aria-label="Remove this branch"
                    (click)="branchRemoveRequested.emit(branch.child.id)"
                    class="ml-auto flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:hover:bg-red-900/20"
                  >
                    <mat-icon class="!text-sm !w-4 !h-4 !leading-4">close</mat-icon>
                  </button>
                </div>
              } @empty {
                <div class="flex flex-col items-center gap-1 rounded-lg border border-dashed border-zinc-200 dark:border-zinc-700 px-3 py-5 text-center">
                  <mat-icon class="!text-lg !w-5 !h-5 !leading-5 text-zinc-300 dark:text-zinc-600">account_tree</mat-icon>
                  <p class="text-xs text-zinc-400 dark:text-zinc-500">
                    No branches yet — add a <span class="font-medium text-zinc-500 dark:text-zinc-400">decision</span> or
                    <span class="font-medium text-zinc-500 dark:text-zinc-400">leaf</span> branch above.
                  </p>
                </div>
              }
            </div>
          </section>
        </div>
      } @else {
        <div class="space-y-5 p-4" [attr.data-testid]="'node-editor-leaf-' + node().id">
          <section class="space-y-4">
            <h3 class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
              <mat-icon class="!text-[13px] !w-3.5 !h-3.5 !leading-[14px]">eco</mat-icon>
              Leaf details
            </h3>
            <label class="block">
              <span class="mb-1 flex items-center gap-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">
                Leaf label
                <span class="text-red-500" aria-hidden="true">*</span>
              </span>
              <input
                data-testid="node-editor-leaf-label"
                type="text"
                required
                aria-required="true"
                [attr.aria-invalid]="isLeafLabelMissing()"
                [ngModel]="node().label"
                (ngModelChange)="patchNode({ label: $event || undefined })"
                placeholder="Required — name this leaf"
                [class]="'w-full rounded-md border bg-white dark:bg-zinc-700 px-2 py-1 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 ' + (isLeafLabelMissing()
                  ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
                  : 'border-zinc-200 dark:border-zinc-600 focus:ring-blue-500')"
              />
            </label>

            <!-- Leaf anchor set (multi-select) -->
            <div>
              <span class="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">Leaf anchors</span>
              <span class="mb-1.5 block text-[11px] text-zinc-500 dark:text-zinc-400">
                Anchor fields scored when matching documents at this leaf. Tap to toggle.
              </span>
              <div class="flex flex-wrap gap-1.5">
                @for (field of anchorFields(); track field.id) {
                  <button
                    type="button"
                    [attr.data-testid]="'node-editor-leaf-anchor-toggle-' + field.id"
                    [attr.aria-pressed]="leaf().leafAnchorFieldIds.includes(field.id)"
                    (click)="toggleLeafAnchor(field.id)"
                    [class]="leaf().leafAnchorFieldIds.includes(field.id)
                      ? 'inline-flex items-center gap-1 rounded-full border border-indigo-400 dark:border-indigo-500 bg-indigo-100 dark:bg-indigo-900/40 px-2 py-0.5 text-[11px] font-medium text-indigo-700 dark:text-indigo-300 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
                      : 'inline-flex items-center gap-1 rounded-full border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-2 py-0.5 text-[11px] font-medium text-zinc-500 dark:text-zinc-400 hover:border-indigo-300 hover:text-indigo-600 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'"
                  >
                    <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">{{ leaf().leafAnchorFieldIds.includes(field.id) ? 'check' : 'add' }}</mat-icon>
                    {{ field.name }}
                  </button>
                } @empty {
                  <span class="text-[11px] text-zinc-400 dark:text-zinc-500">No anchor fields defined yet.</span>
                }
                @for (unknownId of unknownLeafAnchorIds(); track unknownId) {
                  <span class="inline-flex items-center gap-1 rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-300"
                        [attr.data-testid]="'node-editor-leaf-unknown-anchor-' + unknownId">
                    <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">warning</mat-icon>
                    unknown field ({{ unknownId }})
                    <button type="button" class="cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" (click)="toggleLeafAnchor(unknownId)" matTooltip="Remove" aria-label="Remove unknown anchor field">
                      <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">close</mat-icon>
                    </button>
                  </span>
                }
              </div>
            </div>
          </section>

          <div class="border-t border-zinc-100 dark:border-zinc-700"></div>

          <div class="flex items-center gap-2 rounded-lg bg-zinc-50 dark:bg-zinc-700/40 px-3 py-2 text-[11px] text-zinc-500 dark:text-zinc-400"
               data-testid="node-editor-leaf-template-count"
               matTooltip="Assign templates on the Templates page">
            <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-[14px] text-zinc-400">description</mat-icon>
            <span><span class="font-semibold tabular-nums text-zinc-700 dark:text-zinc-200">{{ leaf().templateIds.length }}</span> template(s) — assign on the Templates page</span>
          </div>
        </div>
      }
    </div>
  `
})
export class NodeInspectorComponent {
  readonly store = inject(OcrPrimeStore);

  node = input.required<AnchorTreeNode>();
  changed = output<AnchorTreeNode>();
  addBranchRequested = output<'decision' | 'leaf'>();
  /** Child node id of the branch to remove — the page confirms and removes. */
  branchRemoveRequested = output<string>();
  closeRequested = output<void>();

  readonly anchorFields = computed(() => this.store.fields().filter(f => f.category === 'anchor'));

  /** Only enum (string + enumValues) and boolean anchor fields can drive a decision node. */
  readonly decisionEligibleFields = computed(() =>
    this.anchorFields().filter(f => this.isDecisionEligible(f))
  );

  decision = computed(() => this.node() as AnchorTreeDecisionNode);
  leaf = computed(() => this.node() as AnchorTreeLeafNode);

  readonly inspectorTitle = computed(() => {
    const node = this.node();
    if (node.label) {
      return node.label;
    }
    if (node.kind === 'decision') {
      return this.decisiveField()?.name ?? 'Unconfigured decision';
    }
    return 'Untitled leaf';
  });

  readonly isLeafLabelMissing = computed(
    () => this.node().kind === 'leaf' && !this.leaf().label?.trim()
  );

  /** The currently selected decisive anchor field, if it still exists. */
  readonly decisiveField = computed(() =>
    this.anchorFields().find(f => f.id === this.decision().decisiveAnchorFieldId)
  );

  /** How branch expected values should be edited for the selected field. */
  readonly decisiveFieldKind = computed<'boolean' | 'enum' | 'other'>(() => {
    const field = this.decisiveField();
    if (!field) return 'other';
    if (field.type === 'boolean') return 'boolean';
    if (field.enumValues?.length) return 'enum';
    return 'other';
  });

  readonly decisiveEnumValues = computed(() => this.decisiveField()?.enumValues ?? []);

  /** Possible values (enum values or true/false) not yet assigned to any branch. */
  readonly uncoveredValues = computed<string[]>(() => {
    const kind = this.decisiveFieldKind();
    if (kind === 'other') return [];
    const all = kind === 'boolean' ? ['true', 'false'] : this.decisiveEnumValues();
    const used = new Set(this.decision().branches.map(b => b.expectedValue));
    return all.filter(v => !used.has(v));
  });

  /**
   * Enum values selectable for the branch at `index`: values already picked by
   * other branches are hidden (the branch's own value stays selectable).
   */
  availableEnumValues(index: number): string[] {
    const usedByOthers = new Set(
      this.decision().branches
        .filter((_, i) => i !== index)
        .map(b => b.expectedValue)
    );
    return this.decisiveEnumValues().filter(v => !usedByOthers.has(v));
  }

  /** Whether a value is already the expected value of another branch. */
  isValueTakenByOtherBranch(index: number, value: string): boolean {
    return this.decision().branches.some((b, i) => i !== index && b.expectedValue === value);
  }

  isUnknownDecisiveField = computed(() => {
    const id = this.decision().decisiveAnchorFieldId;
    return !!id && !this.anchorFields().some(f => f.id === id);
  });

  /** Field exists but is not enum/boolean (legacy trees). */
  isIneligibleDecisiveField = computed(() => {
    const field = this.decisiveField();
    return !!field && !this.isDecisionEligible(field);
  });

  unknownLeafAnchorIds = computed(() => {
    const known = new Set(this.anchorFields().map(f => f.id));
    return (this.leaf().leafAnchorFieldIds ?? []).filter(id => !known.has(id));
  });

  patchNode(patch: Partial<AnchorTreeNode>): void {
    this.changed.emit({ ...this.node(), ...patch } as AnchorTreeNode);
  }

  patchDecision(patch: Partial<AnchorTreeDecisionNode>): void {
    this.changed.emit({ ...this.decision(), ...patch });
  }

  /**
   * Switch the decisive anchor field; clears branch expected values that are
   * not valid for the newly selected field (enum values / true|false).
   */
  onDecisiveFieldChange(decisiveAnchorFieldId: string): void {
    const field = this.anchorFields().find(f => f.id === decisiveAnchorFieldId);
    const validValues =
      field?.type === 'boolean' ? ['true', 'false'] : field?.enumValues ?? [];
    const branches = this.decision().branches.map(b =>
      validValues.includes(b.expectedValue) ? b : { ...b, expectedValue: '' }
    );
    this.patchDecision({ decisiveAnchorFieldId, branches });
  }

  normalizeThreshold(value: unknown): number | undefined {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (Number.isNaN(num)) {
      return undefined;
    }
    return Math.min(1, Math.max(0, num));
  }

  patchBranch(index: number, expectedValue: string): void {
    const branches = this.decision().branches.map((b, i) =>
      i === index ? { ...b, expectedValue } : b
    );
    this.patchDecision({ branches });
  }

  toggleLeafAnchor(fieldId: string): void {
    const current = this.leaf().leafAnchorFieldIds ?? [];
    const leafAnchorFieldIds = current.includes(fieldId)
      ? current.filter(id => id !== fieldId)
      : [...current, fieldId];
    this.changed.emit({ ...this.leaf(), leafAnchorFieldIds });
  }

  private isDecisionEligible(field: Field): boolean {
    return field.type === 'boolean' || (field.type === 'string' && !!field.enumValues?.length);
  }
}
